<script lang="ts">
export enum displayMode {
  Fixed = 'fixed',
  Absolute = 'absolute',
}
</script>

<script setup lang="ts">
import { computed, onMounted, ref, type PropType, useId, watch } from 'vue';
import { useFloating, autoUpdate, offset, flip, shift, size } from '@floating-ui/vue';
import { FocusScope } from 'reka-ui';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String as PropType<displayMode>,
    default: displayMode.Absolute,
  },
  customStyles: {
    type: String,
    default: null,
  },
  onClose: {
    type: Function as PropType<() => void>,
    default: null,
  },
  onOpen: {
    type: Function as PropType<() => void>,
    default: null,
  },
  target: {
    type: String,
    default: 'body'
  }
});

const containerRef = ref();
const isMounted = ref();
const dropdownWrapper = ref<HTMLElement>();
const dropdownContent = ref<HTMLElement>();
const dropdownButton = ref<HTMLElement>();
const dropdownOpen = ref(false);
const panelId = useId();
let elementFocusedBeforeOpen: HTMLElement | null = null;

// The trigger is the consumer's own element inside the #button slot, so its state is handed over
// for the consumer to bind rather than set on the wrapper, which is not what a screen reader reads.
const triggerProps = computed(() => ({
  'aria-expanded': dropdownOpen.value,
  'aria-controls': dropdownOpen.value ? panelId : undefined,
}));

const triggerElement = () =>
  dropdownButton.value?.querySelector<HTMLElement>('button, a[href], input, [tabindex]:not([tabindex="-1"])');

const { floatingStyles } = useFloating(dropdownButton, dropdownContent, {
  whileElementsMounted: autoUpdate,
  placement: 'bottom',
  middleware: [
    offset(0),
    flip(),
    shift({ padding: 20 }),
    size({
      padding: 20,
      apply({ availableHeight, elements }) {
        elements.floating.style.maxHeight = `${Math.max(0, availableHeight)}px`;
      },
    }),
  ],
});

const detectClickOutside = (e: MouseEvent) => {
  e.stopPropagation();
  if (dropdownWrapper.value == e.target) closeDropdown();
};

const handleDropdownOpen = () => {
  if (!props.disabled) {
    dropdownOpen.value = !dropdownOpen.value;
  }
};

// The panel is teleported out of any surrounding focus trap — a modal dialog's, say — which would
// otherwise pull focus straight back out of it. Mounting a FocusScope pauses that trap, and reka-ui
// raises this event only once it has: the first moment a consumer can focus the panel and have it
// stick. A consumer that focuses nothing leaves it to reka-ui, which focuses the first tabbable
// element, or the panel itself; focus left on the trigger would sit outside the trap.
const handleMountAutoFocus = (event: Event) => {
  elementFocusedBeforeOpen = document.activeElement as HTMLElement | null;
  if (props.onOpen) props.onOpen();
  if (dropdownContent.value?.contains(document.activeElement)) event.preventDefault();
};

// reka-ui's own restore selects the text of any input it returns focus to, which the next keystroke
// would then replace, so focus is put back by hand. The trigger is preferred over whatever had focus
// at open: Safari does not focus a clicked button, and a consumer that focuses its panel while it
// renders skips the mount event altogether.
const handleUnmountAutoFocus = (event: Event) => {
  event.preventDefault();
  (triggerElement() ?? elementFocusedBeforeOpen)?.focus();
  elementFocusedBeforeOpen = null;
};

const closeDropdown = () => {
  dropdownOpen.value = false;
  if (props.onClose) props.onClose();
};

// A surrounding dialog closes on any Escape that reaches the window; this one belongs to the panel.
const handleEscape = (event: KeyboardEvent) => {
  event.stopPropagation();
  closeDropdown();
};

onMounted(() => {
  isMounted.value = true;
});

watch(dropdownContent, () => {
  if (dropdownContent.value) {
    document.addEventListener('click', detectClickOutside);
  } else {
    document.removeEventListener('click', detectClickOutside);
  }
});
</script>

<template>
  <div
    ref="containerRef"
    class="relative"
  >
    <div
      ref="dropdownButton"
      @click="handleDropdownOpen()"
    >
      <slot
        name="button"
        :is-open="dropdownOpen"
        :trigger-props="triggerProps"
      />
    </div>
    <teleport
      v-if="isMounted"
      :to="props.target"
    >
      <transition
        enter-active-class="transition-all ease-out"
        leave-active-class="transition-all ease-in"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <!-- A modal dialog makes everything outside itself inert and dismisses on any pointerdown
             that reaches the document, so this teleported layer opts back into pointer events and
             stops its own from getting there. A non-modal one dismisses on focus instead, which
             this does not cover. -->
        <div
          v-if="dropdownOpen"
          ref="dropdownWrapper"
          class="fixed top-0 left-0 bottom-0 right-0 z-[10000] pointer-events-auto"
          @click="detectClickOutside"
          @pointerdown.stop
        >
          <FocusScope
            as-child
            trapped
            loop
            @mount-auto-focus="handleMountAutoFocus"
            @unmount-auto-focus="handleUnmountAutoFocus"
          >
            <div
              :id="panelId"
              ref="dropdownContent"
              :style="floatingStyles"
              class="z-[10001] flex flex-col overflow-hidden rounded-lg bg-popover text-popover-foreground shadow-lg mr-10"
              :class="[customStyles, mode]"
              @keydown.escape="handleEscape"
              @touchstart.stop
              @touchmove.stop
              @touchend.stop
            >
              <slot :close="closeDropdown" />
            </div>
          </FocusScope>
        </div>
      </transition>
    </teleport>
  </div>
</template>
